/**
 * Shared form fields for QR code create/edit.
 *
 * Static/dynamic creation is limited per plan (static_qr_limit /
 * dynamic_qr_limit); the API rejects creation beyond the limit with a
 * localized message.
 */

import {
  AutocompleteArrayInput,
  BooleanInput,
  DateTimeInput,
  SelectInput,
  TextInput,
  required,
  useGetList,
  useTranslate,
} from 'react-admin';
import { Alert } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { hasDevice } from '../routingHealth';

const OrgScopedInputs = () => {
  const organizationId = useWatch({ name: 'organization_id' });
  const noExpiry = useWatch({ name: 'no_expiry' });
  const selectedDefault = useWatch({ name: 'default_staff_id' });
  const translate = useTranslate();

  const { data: departments } = useGetList('departments', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  const { data: staff } = useGetList('staff', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  const departmentChoices = (departments ?? [])
    .filter((dept: any) => !organizationId || dept.organization_id === organizationId)
    .map((dept: any) => ({ id: dept.department_id, name: dept.name }));
  const staffChoices = (staff ?? []).map((member: any) => ({
    id: member.user_id,
    name: member.full_name,
  }));

  // DEFAULT STAFF (#54): only agents that can actually answer — role-3 staff (already the
  // 'staff' resource) WITH a registered device — are selectable. The stored value is kept
  // visible even when it cannot answer, so editing an existing (mis)configured QR shows the
  // problem instead of silently blanking the field.
  const answerableStaff = (staff ?? []).filter(hasDevice);
  const defaultStaffChoices = answerableStaff.map((member: any) => ({
    id: member.user_id,
    name: member.full_name,
  }));
  const selectedDefaultId = selectedDefault != null && selectedDefault !== '' ? Number(selectedDefault) : null;
  const defaultCanAnswer =
    selectedDefaultId == null || answerableStaff.some((m: any) => Number(m.user_id) === selectedDefaultId);
  if (selectedDefaultId != null && !defaultCanAnswer) {
    const current = (staff ?? []).find((m: any) => Number(m.user_id) === selectedDefaultId);
    defaultStaffChoices.unshift({
      id: selectedDefaultId,
      name: `${current?.full_name || `#${selectedDefaultId}`} — ${translate('resources.qrcodes.default_staff_cannot_answer_suffix')}`,
    });
  }

  return (
    <>
      <AutocompleteArrayInput
        source="department_id"
        choices={departmentChoices}
        label={translate('resources.qrcodes.fields.department_id')}
        helperText={translate('resources.qrcodes.department_help')}
      />
      <AutocompleteArrayInput
        source="staffs"
        choices={staffChoices}
        label={translate('resources.qrcodes.fields.staffs')}
        helperText={translate('resources.qrcodes.staffs_help')}
      />
      <SelectInput
        source="default_staff_id"
        choices={defaultStaffChoices}
        helperText={translate('resources.qrcodes.default_staff_help')}
      />
      {selectedDefaultId != null && !defaultCanAnswer && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('resources.qrcodes.default_staff_warning')}
        </Alert>
      )}
      <DateTimeInput source="qr_start_time" validate={required()} />
      <BooleanInput source="no_expiry" label={translate('resources.qrcodes.fields.no_expiry')} />
      {!noExpiry && <DateTimeInput source="qr_end_time" />}
      <BooleanInput
        source="enabledDirectCall"
        format={(value: any) => Boolean(value)}
        parse={(value: any) => (value ? 1 : 0)}
      />
      {/* #60: per-QR override for the callback contact form. The backend defaults new rows
          to "shown" (1); tolerate the field being absent (older API without the migration)
          by treating it the same as that default so an unmigrated environment keeps working. */}
      <BooleanInput
        source="showCallbackForm"
        label={translate('resources.qrcodes.fields.showCallbackForm')}
        helperText={translate('resources.qrcodes.show_callback_form_help')}
        format={(value: any) => (value === undefined || value === null ? true : Boolean(value))}
        parse={(value: any) => (value ? 1 : 0)}
      />
    </>
  );
};

export const QrCodeFormFields = ({ isCreate }: { isCreate: boolean }) => {
  const translate = useTranslate();
  const { data: organizations } = useGetList('organizations', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  return (
    <>
      {isCreate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('resources.qrcodes.plan_limit_note')}
        </Alert>
      )}
      <TextInput source="qr_name" validate={required()} />
      <SelectInput
        source="type"
        validate={required()}
        disabled={!isCreate}
        choices={[
          { id: 'static', name: translate('resources.qrcodes.types.static') },
          { id: 'dynamic', name: translate('resources.qrcodes.types.dynamic') },
        ]}
      />
      {!isCreate && (
        <SelectInput
          source="status"
          choices={[
            { id: 'active', name: translate('common.status.active', { _: 'Activo' }) },
            { id: 'inactive', name: translate('common.status.inactive', { _: 'Inactivo' }) },
          ]}
        />
      )}
      <SelectInput
        source="organization_id"
        validate={required()}
        disabled={!isCreate}
        choices={(organizations ?? []).map((org: any) => ({
          id: org.user_id,
          name: org.company_name || org.full_name,
        }))}
      />
      <OrgScopedInputs />
    </>
  );
};

export default QrCodeFormFields;
